import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

/**
 * Premium typeable time input with an OPTIONAL compact picker.
 *
 * Typing is primary — the field is a normal text input, not readOnly.
 * User types "5", "5pm", "5:30 PM", "17:30", "530p", etc. On blur or
 * Enter the value is parsed, normalised to 24h "HH:mm" for storage,
 * and displayed as 12h "5:30 PM". Invalid input surfaces an inline
 * hint without destroying what the user typed.
 *
 * The picker is opened ONLY by clicking the clock icon. Focus alone
 * does NOT open the picker — typing is uninterrupted. Parent owns the
 * open state via `open` + `onOpen` + `onClose` so paired fields
 * (Start / End) can be mutually exclusive without a shared registry.
 *
 * Value contract: 24h "HH:mm" in and out. Formik / backend payload
 * shape unchanged from previous implementations.
 */

const T = {
  navy: "#0d1b2a",
  white: "#ffffff",
  bg: "#f7f5f1",
  slate: "#5c6b7e",
  slateLt: "#9ca8b7",
  line: "#e5e9ef",
  primary: "#1F6FEB",
  primarySoft: "#E6EFFB",
  error: "#D14343",
} as const;

const pad2 = (n: number) => String(n).padStart(2, "0");

const parse24 = (v?: string): { h24: number; minute: number } | null => {
  if (!v) return null;
  const parts = String(v).split(":");
  if (parts.length < 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return { h24: h, minute: m };
};

const fmt12 = (v?: string): string => {
  const p = parse24(v);
  if (!p) return "";
  const isPM = p.h24 >= 12;
  const h12 = p.h24 % 12 || 12;
  return `${h12}:${pad2(p.minute)} ${isPM ? "PM" : "AM"}`;
};

const to24 = (h12: number, minute: number, isPM: boolean): string => {
  let h = h12 % 12;
  if (isPM) h += 12;
  return `${pad2(h)}:${pad2(minute)}`;
};

/**
 * Parse a user-typed time string. Accepts:
 *   "5", "5p", "5pm", "5 PM"          → 05:00 or 17:00 depending on AM/PM
 *   "5:30", "05:30 PM", "17:30"
 *   "530", "530p", "1130a"            (shorthand)
 * Rejects: 25:00, 13:70, 0 PM, 14 PM, letters.
 *
 * Ambiguous case: bare 1-12 with no AM/PM. If `prevValue` has AM/PM
 * context, that is preserved. Otherwise returns "ambiguous" so the UI
 * can hint the user to add AM/PM rather than silently defaulting.
 */
type ParseResult =
  | { ok: true; h24: number; minute: number }
  | { ok: false; reason: "invalid" | "ambiguous" };

function parseTimeInput(raw: string, prevValue?: string): ParseResult {
  const s = raw.trim().toLowerCase().replace(/\s+/g, "");
  if (!s) return { ok: false, reason: "invalid" };

  // AM/PM suffix (a, p, am, pm)
  const apMatch = s.match(/(am|pm|a|p)$/);
  const ap: "am" | "pm" | null = apMatch
    ? (apMatch[1].startsWith("p") ? "pm" : "am")
    : null;
  const nums = ap ? s.slice(0, apMatch!.index) : s;

  let h: number;
  let m: number;

  if (nums.includes(":")) {
    // "5:30", "05:30", "17:30"
    const parts = nums.split(":");
    if (parts.length !== 2) return { ok: false, reason: "invalid" };
    h = Number(parts[0]);
    m = Number(parts[1]);
  } else {
    // Shorthand: pure digits, 1–4 chars. "5" → 5:00; "530" → 5:30;
    // "1130" → 11:30; "1234" → 12:34.
    if (!/^\d{1,4}$/.test(nums)) return { ok: false, reason: "invalid" };
    if (nums.length <= 2) {
      h = Number(nums);
      m = 0;
    } else {
      h = Number(nums.slice(0, nums.length - 2));
      m = Number(nums.slice(nums.length - 2));
    }
  }

  if (Number.isNaN(h) || Number.isNaN(m)) return { ok: false, reason: "invalid" };
  if (m < 0 || m > 59) return { ok: false, reason: "invalid" };

  if (ap) {
    // AM/PM specified — hour must be 1–12.
    if (h < 1 || h > 12) return { ok: false, reason: "invalid" };
    return { ok: true, h24: (h % 12) + (ap === "pm" ? 12 : 0), minute: m };
  }

  // No AM/PM. Ranges:
  //   0 or 13–23 → unambiguous 24-hour
  //   1–12       → ambiguous
  if (h === 0 || (h >= 13 && h <= 23)) {
    return { ok: true, h24: h, minute: m };
  }
  if (h >= 1 && h <= 12) {
    const prev = parse24(prevValue);
    if (prev) {
      const prevIsPM = prev.h24 >= 12;
      return { ok: true, h24: (h % 12) + (prevIsPM ? 12 : 0), minute: m };
    }
    return { ok: false, reason: "ambiguous" };
  }
  return { ok: false, reason: "invalid" };
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  disabled?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  id?: string;
  minuteStep?: number;
  pickerEnabled?: boolean;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

const PremiumTimeInput: React.FC<Props> = ({
  value,
  onChange,
  label = "",
  error = false,
  helperText,
  disabled = false,
  placeholder = "Select or type time",
  fullWidth = true,
  id,
  minuteStep = 5,
  pickerEnabled = true,
  open = false,
  onOpen,
  onClose,
}) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const focusedRef = useRef(false);

  // Local text state — reflects what the user is typing. Regenerated
  // from `value` whenever value changes AND the field is not focused
  // (so external commits from the picker sync in). When focused, we
  // leave the user's typed text alone.
  const [text, setText] = useState<string>(() => fmt12(value));
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!focusedRef.current) {
      setText(fmt12(value));
      setLocalError(null);
    }
  }, [value]);

  const commitText = (): boolean => {
    if (text.trim() === "") {
      // Empty input clears the value.
      if (value !== "") onChange("");
      setLocalError(null);
      return true;
    }
    const result = parseTimeInput(text, value);
    if (result.ok) {
      const next = `${pad2(result.h24)}:${pad2(result.minute)}`;
      if (next !== value) onChange(next);
      setText(fmt12(next));
      setLocalError(null);
      return true;
    }
    if (result.reason === "ambiguous") {
      setLocalError("Add AM or PM (e.g. 5:30 PM)");
    } else {
      setLocalError("Enter a valid time, e.g. 5:30 PM");
    }
    return false;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitText();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setText(fmt12(value));
      setLocalError(null);
      inputRef.current?.blur();
    }
  };

  // ── Picker state (draft-commit) ─────────────────────────────────
  type Draft = { h12: number; minute: number; isPM: boolean };
  const initialDraft = useMemo<Draft>(() => {
    const p = parse24(value);
    if (!p) {
      const now = new Date();
      const step = Math.max(1, Math.min(30, minuteStep));
      const roundedMin = (Math.round(now.getMinutes() / step) * step) % 60;
      const h24 = now.getHours();
      return { h12: h24 % 12 || 12, minute: roundedMin, isPM: h24 >= 12 };
    }
    return { h12: p.h24 % 12 || 12, minute: p.minute, isPM: p.h24 >= 12 };
  }, [value, minuteStep]);
  const [draft, setDraft] = useState<Draft>(initialDraft);

  useEffect(() => {
    if (open) setDraft(initialDraft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const commitDraft = () => {
    const next = to24(draft.h12, draft.minute, draft.isPM);
    onChange(next);
    setText(fmt12(next));
    setLocalError(null);
    onClose?.();
  };

  const setDraftNow = () => {
    const now = new Date();
    const step = Math.max(1, Math.min(30, minuteStep));
    const roundedMin = (Math.round(now.getMinutes() / step) * step) % 60;
    const h24 = now.getHours();
    setDraft({ h12: h24 % 12 || 12, minute: roundedMin, isPM: h24 >= 12 });
  };

  const HOURS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const MINUTES = useMemo(() => {
    const step = Math.max(1, Math.min(30, minuteStep));
    return Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step).filter((m) => m < 60);
  }, [minuteStep]);

  const showError = error || !!localError;
  const displayHelper = localError ?? helperText;

  return (
    <ClickAwayListener onClickAway={() => { if (open) onClose?.(); }}>
      <Box ref={anchorRef} sx={{ position: "relative", width: fullWidth ? "100%" : "auto" }}>
        <TextField
          id={id}
          inputRef={inputRef}
          label={label || undefined}
          fullWidth={fullWidth}
          size="small"
          value={text}
          placeholder={placeholder}
          error={showError}
          helperText={displayHelper}
          disabled={disabled}
          onChange={(e) => {
            setText(e.target.value);
            if (localError) setLocalError(null);
          }}
          onFocus={() => { focusedRef.current = true; }}
          onBlur={() => {
            focusedRef.current = false;
            commitText();
          }}
          onKeyDown={handleKeyDown}
          InputLabelProps={label ? { shrink: !!text || focusedRef.current } : undefined}
          InputProps={{
            endAdornment: pickerEnabled ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  disabled={disabled}
                  aria-label={`Open ${label || "time"} picker`}
                  onMouseDown={(e) => e.preventDefault()} // don't steal focus from input
                  onClick={() => {
                    if (open) onClose?.();
                    else onOpen?.();
                  }}
                  edge="end"
                  sx={{ cursor: "pointer" }}
                >
                  <AccessTimeIcon sx={{ fontSize: 18, color: open ? T.primary : T.slateLt }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              minHeight: 44,
              borderRadius: "10px",
              bgcolor: T.white,
              "& fieldset": { borderColor: T.line },
              "&:hover fieldset": { borderColor: T.slateLt },
              "&.Mui-focused fieldset": { borderColor: T.primary, borderWidth: 2 },
              "&.Mui-error fieldset": { borderColor: T.error },
            },
            "& input": { cursor: "text", fontSize: 14, color: T.navy, py: 1 },
          }}
        />

        {pickerEnabled && (
          <Popper
            open={open && !disabled}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            modifiers={[{ name: "preventOverflow", options: { padding: 8 } }]}
            style={{ zIndex: 1600 }}
          >
            <Box
              sx={{
                mt: 0.75,
                bgcolor: T.white,
                borderRadius: "14px",
                border: `1px solid ${T.line}`,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 12px 40px rgba(0,0,0,0.04)",
                overflow: "hidden",
                width: 280,
                userSelect: "none",
              }}
            >
              <Box sx={{
                display: "flex", alignItems: "center", gap: 0.75, px: 2, py: 1.1,
                borderBottom: `1px solid ${T.line}`,
              }}>
                <AccessTimeIcon sx={{ fontSize: 14, color: T.primary }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.navy }}>
                  {draft.h12}:{pad2(draft.minute)} {draft.isPM ? "PM" : "AM"}
                </Typography>
              </Box>

              <Box sx={{ p: 1.25 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slate, letterSpacing: "0.06em", textTransform: "uppercase", mb: 0.5 }}>
                  Hour
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0.5, mb: 1.25 }}>
                  {HOURS.map((h) => {
                    const active = h === draft.h12;
                    return (
                      <Box
                        key={h}
                        role="button"
                        aria-pressed={active}
                        tabIndex={0}
                        onClick={() => setDraft((d) => ({ ...d, h12: h }))}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDraft((d) => ({ ...d, h12: h })); } }}
                        sx={{
                          py: 0.6, textAlign: "center", borderRadius: "8px", cursor: "pointer",
                          bgcolor: active ? T.primary : "transparent",
                          color: active ? T.white : T.navy,
                          fontWeight: active ? 700 : 500,
                          fontSize: 13,
                          transition: "background-color 120ms",
                          "&:hover": { bgcolor: active ? T.primary : T.primarySoft },
                        }}
                      >
                        {h}
                      </Box>
                    );
                  })}
                </Box>

                <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slate, letterSpacing: "0.06em", textTransform: "uppercase", mb: 0.5 }}>
                  Minute
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0.5, mb: 1.25 }}>
                  {MINUTES.map((m) => {
                    const active = m === draft.minute;
                    return (
                      <Box
                        key={m}
                        role="button"
                        aria-pressed={active}
                        tabIndex={0}
                        onClick={() => setDraft((d) => ({ ...d, minute: m }))}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDraft((d) => ({ ...d, minute: m })); } }}
                        sx={{
                          py: 0.5, textAlign: "center", borderRadius: "8px", cursor: "pointer",
                          bgcolor: active ? T.primary : "transparent",
                          color: active ? T.white : T.navy,
                          fontWeight: active ? 700 : 500,
                          fontSize: 12,
                          fontVariantNumeric: "tabular-nums",
                          transition: "background-color 120ms",
                          "&:hover": { bgcolor: active ? T.primary : T.primarySoft },
                        }}
                      >
                        {pad2(m)}
                      </Box>
                    );
                  })}
                </Box>

                <Box sx={{
                  display: "grid", gridTemplateColumns: "1fr 1fr",
                  border: `1px solid ${T.line}`, borderRadius: "8px", overflow: "hidden",
                }}>
                  {(["AM", "PM"] as const).map((p) => {
                    const active = (p === "PM") === draft.isPM;
                    return (
                      <Box
                        key={p}
                        role="button"
                        aria-pressed={active}
                        tabIndex={0}
                        onClick={() => setDraft((d) => ({ ...d, isPM: p === "PM" }))}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDraft((d) => ({ ...d, isPM: p === "PM" })); } }}
                        sx={{
                          py: 0.6, textAlign: "center", cursor: "pointer",
                          bgcolor: active ? T.primary : T.white,
                          color: active ? T.white : T.slate,
                          fontWeight: 700, fontSize: 12,
                          transition: "background-color 120ms",
                          "&:hover": { bgcolor: active ? T.primary : T.primarySoft },
                        }}
                      >
                        {p}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Box sx={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                px: 1.5, py: 0.9, borderTop: `1px solid ${T.line}`, bgcolor: T.bg,
              }}>
                <Typography
                  role="button"
                  tabIndex={0}
                  onClick={setDraftNow}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDraftNow(); } }}
                  sx={{ fontSize: 12, fontWeight: 600, color: T.slate, cursor: "pointer", "&:hover": { color: T.primary } }}
                >
                  Now
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Typography
                    role="button"
                    tabIndex={0}
                    onClick={() => onClose?.()}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClose?.(); } }}
                    sx={{ fontSize: 12, fontWeight: 600, color: T.slate, cursor: "pointer", "&:hover": { color: T.navy } }}
                  >
                    Cancel
                  </Typography>
                  <Typography
                    role="button"
                    tabIndex={0}
                    onClick={commitDraft}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); commitDraft(); } }}
                    sx={{
                      fontSize: 12, fontWeight: 700, color: T.white,
                      bgcolor: T.primary, px: 1.5, py: 0.5, borderRadius: "6px",
                      cursor: "pointer", "&:hover": { filter: "brightness(0.95)" },
                    }}
                  >
                    Apply
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Popper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default PremiumTimeInput;
